import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Mail, Send, Eye, AlertCircle, CheckCircle2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { sendTestEmail, generatePreviewHtml, validateTemplateData } from '@/services/emailTemplateConfigService';

interface SmartTestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template: {
    template_key: string;
    template_name: string;
    required_variables: any;
    sample_data: any;
  } | null;
}

export const SmartTestDialog: React.FC<SmartTestDialogProps> = ({
  open,
  onOpenChange,
  template,
}) => {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [testData, setTestData] = useState('');
  const [isSending, setSending] = useState(false);
  const [isLoadingPreview, setLoadingPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('compose');

  // Initialize test data with sample data
  useEffect(() => {
    if (template?.sample_data) {
      const sampleData = typeof template.sample_data === 'string' 
        ? JSON.parse(template.sample_data as string)
        : template.sample_data;
      setTestData(JSON.stringify(sampleData, null, 2));
    }
  }, [template]);

  // Validate test data
  useEffect(() => {
    if (!template || !testData.trim()) {
      setValidationErrors([]);
      return;
    }

    try {
      const parsedData = JSON.parse(testData);
      const requiredVars = Array.isArray(template.required_variables)
        ? template.required_variables
        : typeof template.required_variables === 'string'
        ? JSON.parse(template.required_variables as string)
        : [];
      
      const missingVars = requiredVars.filter((varName: string) => {
        return !(varName in parsedData);
      });
      
      setValidationErrors(missingVars);
    } catch (e) {
      setValidationErrors(['Invalid JSON format']);
    }
  }, [testData, template]);

  // Load preview when switching to preview tab
  useEffect(() => {
    if (activeTab === 'preview' && !previewHtml && open) {
      loadPreview();
    }
  }, [activeTab, open]);

  const loadPreview = async () => {
    if (!template) return;
    
    setLoadingPreview(true);
    try {
      const sampleData = typeof template.sample_data === 'string'
        ? JSON.parse(template.sample_data as string)
        : template.sample_data;
      const parsedData = testData.trim() ? JSON.parse(testData) : sampleData;
      const html = await generatePreviewHtml(template.template_key, parsedData);
      setPreviewHtml(html);
    } catch (error: any) {
      console.error('Error loading preview:', error);
      toast.error('Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSendTest = async () => {
    if (!template) return;
    
    if (!recipientEmail) {
      toast.error('Please enter a recipient email address');
      return;
    }

    if (validationErrors.length > 0) {
      toast.error('Please fix validation errors before sending');
      return;
    }

    setSending(true);
    try {
      const sampleData = typeof template.sample_data === 'string'
        ? JSON.parse(template.sample_data as string)
        : template.sample_data;
      const parsedData = testData.trim() ? JSON.parse(testData) : sampleData;
      await sendTestEmail(template.template_key, recipientEmail, parsedData);
      toast.success(`Test email sent to ${recipientEmail}`);
      onOpenChange(false);
      setRecipientEmail('');
    } catch (error: any) {
      console.error('Error sending test email:', error);
      toast.error(error.message || 'Failed to send test email');
    } finally {
      setSending(false);
    }
  };

  const handleReset = () => {
    if (!template) return;
    const sampleData = typeof template.sample_data === 'string'
      ? JSON.parse(template.sample_data as string)
      : template.sample_data;
    setTestData(JSON.stringify(sampleData, null, 2));
    toast.success('Reset to sample data');
  };

  const handleRefreshPreview = () => {
    setPreviewHtml('');
    loadPreview();
  };

  if (!template) return null;

  const requiredVariables = Array.isArray(template.required_variables)
    ? template.required_variables
    : typeof template.required_variables === 'string'
    ? JSON.parse(template.required_variables as string)
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Smart Test Email - {template.template_name}
          </DialogTitle>
          <DialogDescription>
            Configure test data, preview the email, and send a test
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="compose" className="gap-2">
              <Mail className="h-4 w-4" />
              Compose
            </TabsTrigger>
            <TabsTrigger value="preview" className="gap-2">
              <Eye className="h-4 w-4" />
              Preview
            </TabsTrigger>
          </TabsList>

          <TabsContent value="compose" className="space-y-4 mt-4">
            <div>
              <Label htmlFor="recipient">Recipient Email *</Label>
              <Input
                id="recipient"
                type="email"
                placeholder="test@example.com"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                className="mt-2"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="test-data">Test Data (JSON)</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                  type="button"
                >
                  Reset to Sample
                </Button>
              </div>
              <Textarea
                id="test-data"
                value={testData}
                onChange={(e) => setTestData(e.target.value)}
                className="font-mono text-sm min-h-[200px]"
                placeholder='{"customerName": "John Doe", "orderNumber": "ORD-12345"}'
              />
            </div>

            {/* Required Variables */}
            {requiredVariables && requiredVariables.length > 0 && (
              <div>
                <Label className="mb-2 block">Required Variables</Label>
                <div className="flex flex-wrap gap-2">
                  {requiredVariables.map((variable: string) => {
                    const isMissing = validationErrors.includes(variable);
                    return (
                      <Badge
                        key={variable}
                        variant={isMissing ? 'destructive' : 'secondary'}
                        className="gap-1"
                      >
                        {isMissing ? (
                          <AlertCircle className="h-3 w-3" />
                        ) : (
                          <CheckCircle2 className="h-3 w-3" />
                        )}
                        {variable}
                      </Badge>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {validationErrors[0] === 'Invalid JSON format' ? (
                    <span>Invalid JSON format. Please check your syntax.</span>
                  ) : (
                    <span>
                      Missing required variables:{' '}
                      {validationErrors.join(', ')}
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Success State */}
            {validationErrors.length === 0 && testData.trim() && (
              <Alert className="border-green-500/50 bg-green-500/10">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <AlertDescription className="text-green-500">
                  All required variables are present. Ready to send!
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>

          <TabsContent value="preview" className="mt-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  Preview how the email will look with your test data
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefreshPreview}
                  disabled={isLoadingPreview}
                >
                  Refresh Preview
                </Button>
              </div>

              <ScrollArea className="h-[400px] rounded-md border bg-background">
                {isLoadingPreview ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center space-y-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
                      <p className="text-sm text-muted-foreground">
                        Generating preview...
                      </p>
                    </div>
                  </div>
                ) : previewHtml ? (
                  <iframe
                    srcDoc={previewHtml}
                    className="w-full min-h-[400px] border-0"
                    title="Email Preview"
                    sandbox="allow-same-origin"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    No preview available
                  </div>
                )}
              </ScrollArea>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSendTest}
            disabled={isSending || validationErrors.length > 0 || !recipientEmail}
          >
            <Send className="mr-2 h-4 w-4" />
            {isSending ? 'Sending...' : 'Send Test Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
